import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { PROJECTS, PROJECT_FILTERS } from '../data/content';
import { Reveal, Eyebrow } from './Reveal';
import { scrollToId } from '../lib/scroll';

export const Showcase = () => {
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  const visible = PROJECTS.filter((p) => filter === 'all' || p.category === filter);

  return (
    <section id="showcase" data-testid="portfolio-section" className="border-y border-bronze/20 bg-paper">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
        <div className="mb-12 flex flex-col gap-8 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <Eyebrow>Showcase</Eyebrow>
            <Reveal>
              <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-espresso sm:text-4xl lg:text-5xl">
                Work that speaks<br />
                in <span className="italic text-bronze">stone and timber.</span>
              </h2>
            </Reveal>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {PROJECT_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`portfolio-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-all duration-300 ${
                  filter === f.key
                    ? 'border-gold bg-gold text-espresso'
                    : 'border-bronze/30 text-coffee hover:border-goldhover hover:text-bronze'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid gap-x-10 gap-y-14 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className={`group cursor-pointer ${i % 2 === 1 ? 'md:mt-16' : ''}`}
                onClick={() => setActive(p)}
                data-testid={`portfolio-card-${i}`}
              >
                <div
                  className="overflow-hidden shadow-warm-sm"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 26px), calc(100% - 26px) 100%, 0 100%)' }}
                >
                  <div className="relative h-[300px] overflow-hidden sm:h-[420px] md:h-[500px]">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
                    />
                    <span className="absolute right-5 top-5 border border-gold/50 bg-espresso/80 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-gold backdrop-blur">
                      {p.year}
                    </span>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-4 pt-5">
                  <div>
                    <h3 className="font-serif text-2xl font-normal text-espresso md:text-3xl">{p.title}</h3>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-stone">{p.specs}</p>
                  </div>
                  <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center border border-bronze/30 text-bronze transition-all duration-300 group-hover:bg-gold group-hover:text-espresso">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl border-bronze/25 bg-paper p-0" data-testid="portfolio-dialog">
          {active && (
            <div>
              <img src={active.image} alt={active.title} className="h-72 w-full object-cover sm:h-96" />
              <div className="p-6 md:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">{active.year} · Invicto Homes</p>
                <h3 className="mt-3 font-serif text-3xl font-light text-espresso">{active.title}</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-stone">{active.specs}</p>
                <button
                  data-testid="portfolio-view-details-button"
                  onClick={() => { setActive(null); scrollToId('contact'); }}
                  className="mt-6 flex items-center gap-2 bg-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-espresso transition-colors hover:bg-goldhover"
                >
                  Discuss a project like this <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
